import { CONTROL_MODE, CONTROL_MODE_CATEGORY } from '../../config/control';

export function useCharts() {
  function applyStrategyModes(targetGraph, modes) {
    if (!targetGraph && !targetGraph?.series) return;
    if (!modes || modes.length === 0) return;

    targetGraph?.series.filter((s) => s.options.isStrategyMode).forEach((s) => s.remove(false));

    const { min: axisMin, max: axisMax, dataMin, dataMax } = targetGraph.xAxis[0].getExtremes();
    const rangeMin = axisMin ?? dataMin;
    const rangeMax = axisMax ?? dataMax;

    const nonStandardModes = modes.filter(
      (mode) => mode.mode.toUpperCase() !== CONTROL_MODE_CATEGORY.STANDARD
    );

    // Determine which modes overlap the current x-axis range
    const modesInView = nonStandardModes.filter((mode) => {
      const startTs = mode.duration?.startTime
        ? new Date(mode.duration.startTime).getTime()
        : rangeMin;
      const endTs = mode.duration?.endTime ? new Date(mode.duration.endTime).getTime() : rangeMax;
      return startTs <= rangeMax && endTs >= rangeMin;
    });

    // Build the visible category list from in-range modes only
    const visibleCategories = [
      ...new Set(
        modesInView.map(
          (mode) => CONTROL_MODE[mode.mode.toUpperCase()]?.category || CONTROL_MODE_CATEGORY.UNKNOWN
        )
      ),
    ].sort();

    // Render only modes that overlap the current view
    modesInView.forEach((mode) => {
      const startTs = mode.duration?.startTime
        ? new Date(mode.duration.startTime).getTime()
        : rangeMin;
      const endTs = mode.duration?.endTime ? new Date(mode.duration.endTime).getTime() : rangeMax;

      const modeCategory =
        CONTROL_MODE[mode.mode.toUpperCase()]?.category || CONTROL_MODE_CATEGORY.UNKNOWN;

      const categoryIndex = visibleCategories.indexOf(modeCategory);

      targetGraph.addSeries(
        {
          animation: false,
          isStrategyMode: true,
          name: mode.mode,
          category: modeCategory,
          type: 'scatter',
          xAxis: 1,
          yAxis: 1,
          data: [
            [startTs, categoryIndex],
            [endTs, categoryIndex],
          ],
          color: CONTROL_MODE[mode.mode.toUpperCase()]?.color || '#777777',
          lineWidth: 6,
          dataGrouping: { enabled: false },
          showInLegend: false,
          enableMouseTracking: false,
          showInNavigator: false,
          marker: { enabled: false },
          states: { inactive: { opacity: 1 } },
          dataLabels: {
            enabled: true,
            align: 'left',
            x: -7,
            overflow: 'allow',
            crop: false,
            style: {
              color: '#1f2937',
              fontSize: '8px',
              fontWeight: '500',
              textOutline: '2px rgba(255,255,255,0.8)',
              textTransform: 'uppercase',
            },
            formatter: function () {
              if (this.point.index === 0) {
                const name = this.series.name;
                return name.length > 15 ? name.substring(0, 12) + '...' : name;
              }
              return '';
            },
          },
        },
        false
      );
    });

    const hasVisibleStrategies = visibleCategories.length > 0;

    if (hasVisibleStrategies) {
      targetGraph.yAxis[0].update({ top: '25%', height: '75%' }, false);
      targetGraph.yAxis[1].update(
        {
          visible: true,
          top: '0%',
          height: '20%',
          categories: visibleCategories,
          min: -0.5,
          max: visibleCategories.length - 0.5,
          tickPositions: visibleCategories.map((_, i) => i),
          labels: {
            enabled: false,
            style: { fontSize: '9px', color: '#6b7280' },
          },
        },
        false
      );
    } else {
      targetGraph.yAxis[1].update({ visible: false, top: '0%', height: '0%' }, false);
      targetGraph.yAxis[0].update({ top: '0%', height: '100%' }, false);
    }

    targetGraph.redraw();
  }

  function setupTooltipToggle(chart) {
    let tooltipEnabled = true;
    let mouseDownX, mouseDownY;

    const onMouseDown = (e) => {
      mouseDownX = e.clientX;
      mouseDownY = e.clientY;
    };

    const onMouseUp = (e) => {
      const dx = Math.abs(e.clientX - mouseDownX);
      const dy = Math.abs(e.clientY - mouseDownY);
      if (dx < 5 && dy < 5) {
        const rect = chart.container.getBoundingClientRect();
        const chartX = e.clientX - rect.left;
        const chartY = e.clientY - rect.top;
        if (
          chartX >= chart.plotLeft &&
          chartX <= chart.plotLeft + chart.plotWidth &&
          chartY >= chart.plotTop &&
          chartY <= chart.plotTop + chart.plotHeight
        ) {
          tooltipEnabled = !tooltipEnabled;
          chart.tooltip.update({ enabled: tooltipEnabled }, false);
        }
      }
    };

    chart.container.addEventListener('mousedown', onMouseDown);
    chart.container.addEventListener('mouseup', onMouseUp);
  }

  return {
    applyStrategyModes,
    setupTooltipToggle,
  };
}
